/* eslint-disable class-methods-use-this */
const { deliveryTypes: DeliveryType, orderType } = require('pando-constants');
const { freightUnit } = require('../../constants/payments');
const { isEmpty, maxBy, uniq } = require('lodash');
const Helper = require('./utils/helper');

class CustomCharge {
  constructor(includeCustomChargeForOpenIndent = false) {
    this.includeCustomChargeForOpenIndent = includeCustomChargeForOpenIndent;
  }
  /*
  The calculation for compute() goes like this:
    PTL: out of all contracts for an indent max custom charges will be taken
        Example,
            Say i have an indent I1 and there are 2 contracts C1 and C2

            C1 has custom charges  { green_charge: 20, red_charge: 30 }
            C2 has custom charges { green_charge: 70, red_charge: 0 }

            max custom charge here is the one associated with C2, which will be taken

    FTL: out of all contracts for an indent cumulative sum of all custom charges will be taken
        Example,
            Say i have an indent I1 and there are 2 contracts C1 and C2

            C1 has custom charges  { green_charge: 20, red_charge: 30 }
            C2 has custom charges { green_charge: 70, red_charge: 0 }
            cumulative sum of both set of custom charges will be taken which is
            => { green_charge: 20 + 70, red_charge: 30 + 0 }
*/
  compute(masterData) {
    let totalCustomCharges = [];
    if (masterData.orderType === orderType.OPEN && this.includeCustomChargeForOpenIndent) {
      totalCustomCharges = this.computeForOpenIndent(masterData, 'compute');
      return totalCustomCharges;
    }
    const deliveryType = masterData.deliveryType;
    const freightContracts = !isEmpty(masterData.freightCharges) ? masterData.freightCharges : [];
    let sourceGateIds = freightContracts.reduce((acc, contract) => {
      if (contract.sourceGateId) {
        acc.push(contract.sourceGateId.toString());
      }
      return acc;
    }, []);
    sourceGateIds = uniq(sourceGateIds);

    const contractWiseCustomCharges = Helper
      .getContractWiseCustomCharges(masterData, sourceGateIds, freightContracts);
    if (contractWiseCustomCharges.length) {
      if (deliveryType === DeliveryType.PTL) {
        totalCustomCharges = CustomCharge.computeForPTL(contractWiseCustomCharges);
      } else {
        totalCustomCharges = CustomCharge.computeForFTLAnyFreightUnit(contractWiseCustomCharges);
      }
    }
    return totalCustomCharges;
  }

  /*
  The calculation for computeBasedOnDeliveryType() goes like this:
    PTL: same as for compute()

    FTL:
      PER_TRIP: custom charge of contract that has max base freight will be taken
        Say i have an indent I1 and there are 2 contracts C1 and C2

        C1 has custom charges  { green_charge: 20, red_charge: 30 } and base freight is 100
        C2 has custom charges { green_charge: 70, red_charge: 0 } and base freight is 90

        custom charges of C1 will be taken since its base freight is higher

      OTHER UNITS: same as 1
*/
  computeBasedOnDeliveryType(masterData) {
    let totalCustomCharges = [];
    if (masterData.orderType === orderType.OPEN && this.includeCustomChargeForOpenIndent) {
      totalCustomCharges = this.computeForOpenIndent(masterData, 'computeBasedOnDeliveryType');
      return totalCustomCharges;
    } else if (masterData.deliveryType === DeliveryType.PTL) {
      totalCustomCharges = this.compute(masterData);
    } else {
      const freightContracts = !isEmpty(masterData.freightCharges) ? masterData.freightCharges : [];
      if (freightContracts.length) {
        if (freightContracts[0].freightUnit === freightUnit.per_trip) {
          totalCustomCharges = CustomCharge.computeForFTLPerTrip(freightContracts, masterData);
        } else {
          let sourceGateIds = freightContracts.reduce((acc, contract) => {
            if (contract.sourceGateId) {
              acc.push(contract.sourceGateId.toString());
            }
            return acc;
          }, []);
          sourceGateIds = uniq(sourceGateIds);
          const contractWiseCustomCharges = Helper
            .getContractWiseCustomCharges(masterData, sourceGateIds, freightContracts);
          totalCustomCharges = CustomCharge
            .computeForFTLAnyFreightUnit(contractWiseCustomCharges);
        }
      }
    }
    return totalCustomCharges;
  }

  static computeForFTLPerTrip(freightContracts, masterData) {
    const contractIdToCustomChargesMap = masterData.indentCustomCharges
      .reduce((acc, customChargeDetails) => {
        acc[customChargeDetails.contract_id] = customChargeDetails.custom_charges;
        return acc;
      }, {});
    let totalCustomCharges = [];
    const maxChargeEntry = maxBy(freightContracts, 'freightCharge');
    const multipleMaxEntries = freightContracts
      .filter(entry => entry.freightCharge === maxChargeEntry.freightCharge);
    if (multipleMaxEntries.length === 1) {
      totalCustomCharges = contractIdToCustomChargesMap[multipleMaxEntries[0].contractId] || [];
    } else {
      const contractWiseCustomCharges = multipleMaxEntries.reduce((acc, entry) => {
        if (contractIdToCustomChargesMap[entry.contractId]) {
          acc.push(contractIdToCustomChargesMap[entry.contractId]);
        }
        return acc;
      }, []);
      totalCustomCharges = CustomCharge
        .getMaxCustomCharges(contractWiseCustomCharges);
    }
    return totalCustomCharges;
  }

  computeForOpenIndent(masterData, strategyType) {
    const contractWiseCustomCharges = [];
    let totalCustomCharges = [];
    const contractArray = [];
    
    masterData.indentDestinations.forEach((destination) => {
      const contract = destination.contract;

      if (
        !contract ||
    contract.freight_charges <= 0 ||
    contract.freight_unit <= 0
      ) {
        return;
      }

      contractArray.push({
        destinationId: destination.locationId,
        sourceId: destination.sourceLocationId,
        sourceGateId: destination.sourceGateId || null,
        destinationGateId: destination.gateId || null,
        freightCharges: contract.freight_charges,
        contractId: contract.id,
        freightUnit: contract.freight_unit,
      });
    });
    let sourceGateIds = contractArray.reduce((acc, contract) => {
      if (contract.sourceGateId) {
        acc.push(contract.sourceGateId.toString());
      }
      return acc;
    }, []);
    sourceGateIds = uniq(sourceGateIds);
    const contractIdToCustomChargesMap = masterData.indentCustomCharges
      .reduce((acc, customChargeDetails) => {
        if (customChargeDetails.contract_id) {
          acc[customChargeDetails.contract_id] = customChargeDetails;
        } else {
          acc.default = customChargeDetails;
        }
        return acc;
      }, {});
    const perTripContract = contractArray.some(
      contract => contract.freightUnit === freightUnit.per_trip,
    );
    if (perTripContract && masterData.deliveryType === DeliveryType.FTL && strategyType === 'computeBasedOnDeliveryType') {
      const maxChargeEntry = maxBy(contractArray, 'freightCharges');
      const multipleMaxEntries = contractArray
        .filter(entry => entry.freightCharges === maxChargeEntry.freightCharges);
      if (multipleMaxEntries.length === 1 && contractIdToCustomChargesMap[multipleMaxEntries[0].contractId]) {
        totalCustomCharges = contractIdToCustomChargesMap[multipleMaxEntries[0].contractId].custom_charges;
      } else if (multipleMaxEntries.length > 1) {
        const contractWisePerTripCustomCharges = multipleMaxEntries.reduce((acc, entry) => {
          if (contractIdToCustomChargesMap[entry.contractId]) {
            acc.push(contractIdToCustomChargesMap[entry.contractId].custom_charges);
          }
          return acc;
        }, []);
        totalCustomCharges = CustomCharge
          .getMaxCustomCharges(contractWisePerTripCustomCharges);
      } else if (multipleMaxEntries.length === 0 && contractIdToCustomChargesMap.default) {
        totalCustomCharges = contractIdToCustomChargesMap.default.custom_charges;
      }
      return totalCustomCharges;
    }
    masterData.destinations.forEach((destination) => {
      let freightContract = null;

      if (sourceGateIds.length) {
        sourceGateIds.forEach((sourceGateId) => {
          freightContract = contractArray.find((contract) => {
            if (contract.sourceGateId) {
              return contract.sourceGateId.toString() === sourceGateId &&
                 contract.destinationId && contract.destinationId.toString() === destination.locationId.toString();
            }
            return false;
          });
        });
      } else {
        freightContract = contractArray.find(
          contract =>
            contract.destinationId.toString() === destination.locationId.toString(),
        );
      }
      if (freightContract && freightContract.contractId &&
            contractIdToCustomChargesMap[freightContract.contractId]) {
        contractWiseCustomCharges
          .push(contractIdToCustomChargesMap[freightContract.contractId].custom_charges);
      } else if (contractIdToCustomChargesMap.default) {
        contractWiseCustomCharges.push(contractIdToCustomChargesMap.default.custom_charges);
      }
    });
    if (masterData.deliveryType === DeliveryType.PTL) {
      totalCustomCharges = CustomCharge.computeForPTL(contractWiseCustomCharges, this.includeCustomChargeForOpenIndent);
    } else if (masterData.deliveryType === DeliveryType.FTL) {
      totalCustomCharges = CustomCharge.computeForFTLAnyFreightUnit(contractWiseCustomCharges);
    }

    return totalCustomCharges;
  }

  static computeForFTLAnyFreightUnit(contractWiseCustomCharges) {
    /*
      out of all custom charges across all contracts that belongs to indent
      get the cumulative sum of all custom charges
    */
    const customChargeNameToDetailsMap = {};
    for (let i = 0; i < contractWiseCustomCharges.length; i += 1) {
      const customCharges = contractWiseCustomCharges[i];
      for (let j = 0; j < customCharges.length; j += 1) {
        const chargeDetails = customCharges[j];
        if (customChargeNameToDetailsMap[chargeDetails.name]) {
          customChargeNameToDetailsMap[chargeDetails.name].amount += chargeDetails.amount;
        } else {
          customChargeNameToDetailsMap[chargeDetails.name] = chargeDetails;
        }
      }
    }
    return Object.values(customChargeNameToDetailsMap);
  }

  static computeForPTL(contractWiseCustomCharges, includeCustomChargeForOpenIndent = false) {
    /*
      out of all custom charges across all contracts that belongs to indent
      get the set of custom charges which yields maximum total
    */
    return this.getMaxCustomCharges(contractWiseCustomCharges, includeCustomChargeForOpenIndent);
  }

  static getMaxCustomCharges(contractWiseCustomCharges, includeCustomChargeForOpenIndent = false) {
    let [prevMax, maxIndex] = [0, -1];
    for (let i = 0; i < contractWiseCustomCharges.length; i += 1) {
      const customCharges = contractWiseCustomCharges[i];
      let currentTotal = 0;
      for (let j = 0; j < customCharges.length; j += 1) {
        currentTotal += customCharges[j].amount || 0;
      }
      if (prevMax < currentTotal || (prevMax === currentTotal && maxIndex === -1 && includeCustomChargeForOpenIndent)) {
        maxIndex = i;
        prevMax = currentTotal;
      }
    }
    return (maxIndex > -1) ? contractWiseCustomCharges[maxIndex] : [];
  }
}

module.exports = CustomCharge;
